import { useEffect, useCallback, useMemo } from 'react';
import { AnonymousSessionContext, SurveyResponseState, STORAGE_KEYS } from '../types';

/**
 * Hook for managing anonymous survey response sessions
 * Handles localStorage persistence, session creation, and response deduplication
 */
export const useSessionManager = () => {
  // Generate a unique session ID
  const generateSessionId = useCallback((): string => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Get or create session ID for a survey instance
  const getOrCreateSessionId = useCallback(
    (instanceId: string): string => {
      const storageKey = `${STORAGE_KEYS.SESSION_ID}_${instanceId}`;
      let sessionId = localStorage.getItem(storageKey);

      if (!sessionId) {
        sessionId = generateSessionId();
        localStorage.setItem(storageKey, sessionId);
      }

      return sessionId;
    },
    [generateSessionId]
  );

  // Get list of started surveys
  const getStartedSurveys = useCallback((): string[] => {
    try {
      const started = localStorage.getItem(STORAGE_KEYS.STARTED_SURVEYS);
      return started ? JSON.parse(started) : [];
    } catch {
      return [];
    }
  }, []);

  // Get list of completed surveys
  const getCompletedSurveys = useCallback((): string[] => {
    try {
      const completed = localStorage.getItem(STORAGE_KEYS.COMPLETED_SURVEYS);
      return completed ? JSON.parse(completed) : [];
    } catch {
      return [];
    }
  }, []);

  // Check if user has already started this survey
  const hasStartedSurvey = useCallback(
    (instanceId: string): boolean => {
      const startedSurveys = getStartedSurveys();
      return startedSurveys.includes(instanceId);
    },
    [getStartedSurveys]
  );

  // Check if user has already completed this survey
  const hasCompletedSurvey = useCallback(
    (instanceId: string): boolean => {
      const completedSurveys = getCompletedSurveys();
      return completedSurveys.includes(instanceId);
    },
    [getCompletedSurveys]
  );

  // Mark survey as started
  const markSurveyStarted = useCallback(
    (instanceId: string): void => {
      const startedSurveys = getStartedSurveys();
      if (!startedSurveys.includes(instanceId)) {
        startedSurveys.push(instanceId);
        localStorage.setItem(STORAGE_KEYS.STARTED_SURVEYS, JSON.stringify(startedSurveys));
      }
    },
    [getStartedSurveys]
  );

  // Mark survey as completed
  const markSurveyCompleted = useCallback(
    (instanceId: string): void => {
      const completedSurveys = getCompletedSurveys();
      if (!completedSurveys.includes(instanceId)) {
        completedSurveys.push(instanceId);
        localStorage.setItem(STORAGE_KEYS.COMPLETED_SURVEYS, JSON.stringify(completedSurveys));
      }
    },
    [getCompletedSurveys]
  );

  // Save survey response state to localStorage
  const saveResponseState = useCallback((instanceId: string, state: SurveyResponseState): void => {
    const storageKey = `${STORAGE_KEYS.RESPONSE_DATA}_${instanceId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      // Silently handle localStorage errors
    }
  }, []);

  // Load survey response state from localStorage
  const loadResponseState = useCallback((instanceId: string): SurveyResponseState | null => {
    const storageKey = `${STORAGE_KEYS.RESPONSE_DATA}_${instanceId}`;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  }, []);

  // Clear response state from localStorage
  const clearResponseState = useCallback((instanceId: string): void => {
    const storageKey = `${STORAGE_KEYS.RESPONSE_DATA}_${instanceId}`;
    const sessionKey = `${STORAGE_KEYS.SESSION_ID}_${instanceId}`;
    const currentQuestionKey = `${STORAGE_KEYS.CURRENT_QUESTION}_${instanceId}`;

    localStorage.removeItem(storageKey);
    localStorage.removeItem(sessionKey);
    localStorage.removeItem(currentQuestionKey);
  }, []);

  // Save current question index
  const saveCurrentQuestion = useCallback((instanceId: string, questionIndex: number): void => {
    const storageKey = `${STORAGE_KEYS.CURRENT_QUESTION}_${instanceId}`;
    localStorage.setItem(storageKey, questionIndex.toString());
  }, []);

  // Load current question index
  const loadCurrentQuestion = useCallback((instanceId: string): number => {
    const storageKey = `${STORAGE_KEYS.CURRENT_QUESTION}_${instanceId}`;
    const saved = localStorage.getItem(storageKey);
    return saved ? parseInt(saved, 10) : 0;
  }, []);

  // Save user's language preference
  const saveLanguagePreference = useCallback((language: 'en' | 'no'): void => {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
  }, []);

  // Load user's language preference
  const loadLanguagePreference = useCallback((): 'en' | 'no' => {
    const saved = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    return (saved as 'en' | 'no') || 'no';
  }, []);

  // Create session context for API calls
  const createSessionContext = useCallback(
    (instanceId: string, language: 'en' | 'no' = 'no'): AnonymousSessionContext => {
      const sessionId = getOrCreateSessionId(instanceId);
      const userAgent = navigator.userAgent;

      return {
        sessionId,
        surveyInstanceId: instanceId,
        userAgent,
        language,
      };
    },
    [getOrCreateSessionId]
  );

  /**
   * Determine whether the current browser session is allowed to submit a response
   * for the given survey instance.
   *
   * @param instanceId - The unique identifier of the survey instance.
   * @param instanceStatus - Optional latest status of the survey instance as reported
   *   by the backend. When provided and equal to 'CLOSED', submissions are always
   *   rejected, even if the local client state would otherwise allow them. When
   *   omitted (or 'ACTIVE'), the decision is based solely on the local completion
   *   state.
   * @returns true if this browser is allowed to submit a response, otherwise false.
   */
  const canSubmitResponse = useCallback(
    (instanceId: string, instanceStatus?: 'ACTIVE' | 'CLOSED'): boolean => {
      // Check if instance is closed (server-side check takes precedence)
      if (instanceStatus === 'CLOSED') {
        return false;
      }

      // Allow resubmission if not completed yet
      return !hasCompletedSurvey(instanceId);
    },
    [hasCompletedSurvey]
  );

  // Clean up old survey data (older than 7 days)
  const cleanupOldData = useCallback((): void => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage);

      keys.forEach((key) => {
        if (key.startsWith('survey_session_') || key.startsWith('survey_response_')) {
          // Extract timestamp from session ID if possible
          const parts = key.split('_');
          if (parts.length >= 3) {
            const timestamp = parseInt(parts[2], 10);
            if (!isNaN(timestamp) && timestamp < sevenDaysAgo) {
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      // Silently handle cleanup errors
    }
  }, []);

  // Initialize session manager
  useEffect(() => {
    // Cleanup old data on first load
    cleanupOldData();
  }, [cleanupOldData]);

  return useMemo(
    () => ({
      // Session management
      getOrCreateSessionId,
      createSessionContext,

      // Survey state tracking
      hasStartedSurvey,
      hasCompletedSurvey,
      markSurveyStarted,
      markSurveyCompleted,
      canSubmitResponse,

      // Response state persistence
      saveResponseState,
      loadResponseState,
      clearResponseState,

      // Question navigation
      saveCurrentQuestion,
      loadCurrentQuestion,

      // Language preference
      saveLanguagePreference,
      loadLanguagePreference,

      // Cleanup
      cleanupOldData,
    }),
    [
      getOrCreateSessionId,
      createSessionContext,
      hasStartedSurvey,
      hasCompletedSurvey,
      markSurveyStarted,
      markSurveyCompleted,
      canSubmitResponse,
      saveResponseState,
      loadResponseState,
      clearResponseState,
      saveCurrentQuestion,
      loadCurrentQuestion,
      saveLanguagePreference,
      loadLanguagePreference,
      cleanupOldData,
    ]
  );
};
